import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Globe, Zap, CheckCircle2 } from 'lucide-react';

const UnicodeEscapeUnescapeTool = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [mode, setMode] = useState('escape');
  const [escapeFormat, setEscapeFormat] = useState('unicode');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [stats, setStats] = useState({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState('');

  const escapeUnicode = useCallback((text: any) => {
    const startTime = performance.now();
    let result = '';
    let entitiesCount = 0;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const code = char.codePointAt(0) || 0;

      if (code > 0xFFFF) {
        entitiesCount++;
        switch (escapeFormat) {
          case 'unicode':
            result += `\\u{${code.toString(16).toUpperCase()}}`;
            break;
          case 'hex':
            result += `\\x{${code.toString(16).toUpperCase()}}`;
            break;
          case 'decimal':
            result += `&#${code};`;
            break;
          case 'octal':
            result += `\\${code.toString(8)}`;
            break;
          case 'url':
            result += encodeURIComponent(char);
            break;
          case 'html':
            result += `&#x${code.toString(16).toUpperCase()};`;
            break;
          default:
            result += char;
        }
        if (i + 1 < text.length && text.charCodeAt(i + 1) >= 0xDC00 && text.charCodeAt(i + 1) <= 0xDFFF) {
          i++;
        }
      } else if (code > 127) {
        entitiesCount++;
        switch (escapeFormat) {
          case 'unicode':
            result += `\\u${code.toString(16).toUpperCase().padStart(4, '0')}`;
            break;
          case 'hex':
            result += `\\x${code.toString(16).toUpperCase().padStart(2, '0')}`;
            break;
          case 'decimal':
            result += `&#${code};`;
            break;
          case 'octal':
            result += `\\${code.toString(8)}`;
            break;
          case 'url':
            result += encodeURIComponent(char);
            break;
          case 'html':
            result += `&#x${code.toString(16).toUpperCase()};`;
            break;
          default:
            result += char;
        }
      } else {
        switch (char) {
          case '\n':
            if (escapeFormat === 'unicode' || escapeFormat === 'hex') {
              result += '\\n';
              entitiesCount++;
            } else {
              result += char;
            }
            break;
          case '\r':
            if (escapeFormat === 'unicode' || escapeFormat === 'hex') {
              result += '\\r';
              entitiesCount++;
            } else {
              result += char;
            }
            break;
          case '\t':
            if (escapeFormat === 'unicode' || escapeFormat === 'hex') {
              result += '\\t';
              entitiesCount++;
            } else {
              result += char;
            }
            break;
          case '\\':
            if (escapeFormat === 'unicode' || escapeFormat === 'hex') {
              result += '\\\\';
              entitiesCount++;
            } else {
              result += char;
            }
            break;
          default:
            result += char;
        }
      }
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, [escapeFormat]);

  const unescapeUnicode = useCallback((text: any) => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    result = result.replace(/\\u\{([0-9a-fA-F]+)\}/g, (match: any, hex: any) => {
      try {
        const code = parseInt(hex, 16);
        if (code >= 0 && code <= 0x10FFFF) {
          entitiesCount++;
          return String.fromCodePoint(code);
        }
      } catch (e) {
        // Invalid unicode
      }
      return match;
    });

    result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match: any, hex: any) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 0xFFFF) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    result = result.replace(/\\x\{([0-9a-fA-F]+)\}/g, (match: any, hex: any) => {
      try {
        const code = parseInt(hex, 16);
        if (code >= 0 && code <= 0x10FFFF) {
          entitiesCount++;
          return String.fromCodePoint(code);
        }
      } catch (e) {
        // Invalid hex
      }
      return match;
    });

    result = result.replace(/\\x([0-9a-fA-F]{2})/g, (match: any, hex: any) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 255) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    result = result.replace(/&#(\d+);/g, (match: any, dec: any) => {
      const code = parseInt(dec, 10);
      if (code >= 0 && code <= 0x10FFFF) {
        entitiesCount++;
        return String.fromCodePoint(code);
      }
      return match;
    });

    result = result.replace(/&#x([0-9a-fA-F]+);/g, (match: any, hex: any) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 0x10FFFF) {
        entitiesCount++;
        return String.fromCodePoint(code);
      }
      return match;
    });

    result = result.replace(/\\([0-7]{1,6})/g, (match: any, oct: any) => {
      const code = parseInt(oct, 8);
      if (code >= 0 && code <= 0x10FFFF && oct !== '0') {
        entitiesCount++;
        return String.fromCodePoint(code);
      }
      return match;
    });

    result = result.replace(/%[0-9a-fA-F]{2}/g, (match: any) => {
      try {
        const decoded = decodeURIComponent(match);
        if (decoded !== match) {
          entitiesCount++;
          return decoded;
        }
      } catch (e) {
        // Invalid URL encoding
      }
      return match;
    });

    const commonEscapes = [
      { escaped: '\\n', char: '\n' },
      { escaped: '\\r', char: '\r' },
      { escaped: '\\t', char: '\t' },
      { escaped: '\\v', char: '\v' },
      { escaped: '\\f', char: '\f' },
      { escaped: '\\b', char: '\b' },
      { escaped: '\\0', char: '\0' },
      { escaped: '\\\\', char: '\\' }
    ];

    commonEscapes.forEach(({ escaped, char }) => {
      const regex = new RegExp(escaped.replace(/\\/g, '\\\\'), 'g');
      const matches = result.match(regex);
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(regex, char);
      }
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback(() => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapeUnicode : unescapeUnicode;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapeUnicode, unescapeUnicode]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: any) => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = () => {
    const blob = new Blob([outputText], { type: 'text/plain; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-unicode.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: any) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file, 'utf-8');
    }
  };

  const clearAll = () => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleUnicode = () => {
    setInputText(`Hello World! 👋
Multi-language text samples:

English: Hello World!
Spanish: ¡Hola Mundo!
French: Bonjour le Monde!
German: Hallo Welt!
Russian: Привет мир!
Chinese: 你好世界！
Japanese: こんにちは世界！
Korean: 안녕하세요 세계!
Arabic: مرحبا بالعالم!
Hebrew: שלום עולם!
Hindi: नमस्ते दुनिया!
Thai: สวัสดีชาวโลก!

Emojis: 😀 😎 🚀 🌟 ❤️ 🎉 🔥 💻
Mathematical: ∑ ∫ ∞ ≤ ≥ ± × ÷ √ π
Special chars: © ® ™ € £ ¥ § ¶ † ‡
Currency: $ € £ ¥ ₹ ₽ ₩ ₪ ₿
Arrows: → ← ↑ ↓ ↔ ⇒ ⇐ ⇑ ⇓
Greek: α β γ δ ε ζ η θ ι κ λ μ ν ξ ο π ρ σ τ υ φ χ ψ ω`);
  };

  const loadSampleEscaped = () => {
    setInputText(`Hello World! \\ud83d\\udc4b
Multi-language text samples:

English: Hello World!
Spanish: \\u00a1Hola Mundo!
French: Bonjour le Monde!
German: Hallo Welt!
Russian: \\u041f\\u0440\\u0438\\u0432\\u0435\\u0442 \\u043c\\u0438\\u0440!
Chinese: \\u4f60\\u597d\\u4e16\\u754c\\uff01
Japanese: \\u3053\\u3093\\u306b\\u3061\\u306f\\u4e16\\u754c\\uff01
Korean: \\uc548\\ub155\\ud558\\uc138\\uc694 \\uc138\\uacc4!
Arabic: \\u0645\\u0631\\u062d\\u0628\\u0627 \\u0628\\u0627\\u0644\\u0639\\u0627\\u0644\\u0645!

Emojis: \\ud83d\\ude00 \\ud83d\\ude0e \\ud83d\\ude80 \\u2b50 \\u2764\\ufe0f \\ud83c\\udf89
Mathematical: \\u2211 \\u222b \\u221e \\u2264 \\u2265 \\u00b1
Special chars: \\u00a9 \\u00ae \\u2122 \\u20ac \\u00a3 \\u00a5`);
  };

  const analyzeText = () => {
    if (!inputText.trim()) {
      showNotification('No text to analyze!');
      return;
    }

    const unicodeRanges = {
      ascii: 0,
      latin: 0,
      cyrillic: 0,
      arabic: 0,
      chinese: 0,
      japanese: 0,
      korean: 0,
      emoji: 0,
      other: 0
    };

    for (const char of inputText) {
      const code = char.codePointAt(0) || 0;
      
      if (code <= 127) unicodeRanges.ascii++;
      else if (code <= 0xFF) unicodeRanges.latin++;
      else if (code >= 0x0400 && code <= 0x04FF) unicodeRanges.cyrillic++;
      else if (code >= 0x0600 && code <= 0x06FF) unicodeRanges.arabic++;
      else if (code >= 0x4E00 && code <= 0x9FFF) unicodeRanges.chinese++;
      else if (code >= 0x3040 && code <= 0x309F) unicodeRanges.japanese++;
      else if (code >= 0xAC00 && code <= 0xD7AF) unicodeRanges.korean++;
      else if (code >= 0x1F000 && code <= 0x1F9FF) unicodeRanges.emoji++;
      else unicodeRanges.other++;
    }

    const analysis = Object.entries(unicodeRanges)
      .filter(([, count]) => count > 0)
      .map(([range, count]) => `${range}: ${count}`)
      .join(', ');

    showNotification(`Character ranges: ${analysis}`);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900 to-green-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-indigo-500 to-green-600 rounded-lg">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Unicode Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced Unicode processor with multi-format support</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-indigo-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape Unicode</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-green-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape Unicode</span>
            </button>
          </div>
        </div>

        {/* Format Selection */}
        {mode === 'escape' && (
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gray-800 p-1 rounded-lg flex flex-wrap gap-1">
              {[
                { value: 'unicode', label: 'Unicode' },
                { value: 'hex', label: 'Hex' },
                { value: 'decimal', label: 'Decimal' },
                { value: 'octal', label: 'Octal' },
                { value: 'url', label: 'URL' },
                { value: 'html', label: 'HTML' }
              ].map((format) => (
                <button
                  key={format.value}
                  onClick={() => setEscapeFormat(format.value)}
                  className={`px-3 py-1 text-sm rounded transition-all ${
                    escapeFormat === format.value
                      ? 'bg-green-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {format.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-indigo-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Entities</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* Unicode Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Supported Unicode Formats</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Unicode:</strong> \\uXXXX \\u{`{XXXXX}`}
                </div>
                <div className="text-gray-300">
                  <strong>Hexadecimal:</strong> \\xXX \\x{`{XXX}`}
                </div>
                <div className="text-gray-300">
                  <strong>HTML:</strong> &#{`123`}; &#x{`ABC`};
                </div>
                <div className="text-gray-300">
                  <strong>URL:</strong> %XX %XXX
                </div>
                <div className="text-gray-300">
                  <strong>Octal:</strong> \\123 \\7777
                </div>
                <div className="text-gray-300">
                  <strong>Range:</strong> U+0000 - U+10FFFF
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".txt,.json,.xml,.html,.js,.ts"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter text with Unicode characters to escape...' : 'Enter escaped Unicode to unescape...'}
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={analyzeText}
                  disabled={!inputText}
                  className="p-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Globe className="w-4 h-4" />
                </button>
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed Unicode output will appear here..."
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleUnicode}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Text
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Escaped
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\s+/g, ' ').trim())}
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors text-sm"
            >
              Normalize Whitespace
            </button>
            <button
              onClick={analyzeText}
              disabled={!inputText}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              Analyze Characters
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnicodeEscapeUnescapeTool;